import { useQuery, type QueryClient } from '@tanstack/react-query';
import { searchEvents, getEventDetails, getTopCategories, type SearchEventsParams } from '@/services/eventsApi';
import type { Event, EventsSearchResponse, EventDetailResponse, Category } from '@/lib/api';

/**
 * Event Queries
 * 
 * React Query hooks for event data fetching from Bespokian Events API.
 */

// ═══════════════════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════════════════

export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (params: SearchEventsParams) => [...eventKeys.lists(), params] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: number) => [...eventKeys.details(), id] as const,
  categories: () => [...eventKeys.all, 'categories'] as const,
};

// ═══════════════════════════════════════════════════════════════════════════════════
// QUERY HOOKS
// ═══════════════════════════════════════════════════════════════════════════════════

/**
 * Search events with filters
 */
export function useEvents(params: SearchEventsParams = {}, enabled = true) {
  return useQuery({
    queryKey: eventKeys.list(params),
    queryFn: () => searchEvents(params),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (prev) => prev,
  });
}

/**
 * Get event details with tickets
 */
export function useEventDetail(eventId: number | null | undefined, params?: {
  min_price?: number;
  max_price?: number;
  instant_download_only?: boolean;
  eticket_only?: boolean;
}) {
  return useQuery({
    queryKey: eventKeys.detail(eventId!),
    queryFn: () => getEventDetails(eventId!, params),
    enabled: eventId != null,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Get top-level event categories (Concerts, Sports, Theatre, etc.)
 * Returns hierarchical structure with main categories and their children
 */
export function useCategories() {
  return useQuery({
    queryKey: eventKeys.categories(),
    queryFn: getTopCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes - categories don't change often
  });
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PREFETCH HELPERS
// ═══════════════════════════════════════════════════════════════════════════════════

export async function prefetchEvents(
  queryClient: QueryClient,
  params: SearchEventsParams = {}
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: eventKeys.list(params),
    queryFn: () => searchEvents(params),
    staleTime: 30 * 1000,
  });
}

export async function prefetchEventDetail(
  queryClient: QueryClient,
  eventId: number
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: () => getEventDetails(eventId),
    staleTime: 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════════

export type { Event, EventsSearchResponse, EventDetailResponse, SearchEventsParams, Category };
